import { sellingPriceSummary, type SellingPriceSummary } from "@/lib/products";
import { productStockCell, type ProductStockCell } from "@/lib/inventory";
import { sellableStockLabel } from "@/lib/providers/marketplace-reservation";
import { categoryPathName, type Category } from "@/lib/categories";

/**
 * Shared product-list row view model.
 *
 * The desktop table and the mobile/tablet cards MUST consume the exact same
 * authoritative/computed data, so there is no second business-state model and
 * no duplicate stock/price/status logic. This pure builder is the single place
 * that reduces a catalogue row + its variant prices + its inventory balance into
 * the fields both layouts render.
 */

export interface ProductListRowInput {
  id: string;
  name: string;
  reference: string | null;
  /** Legacy product-level SKU mirror — used only as a display fallback. */
  sku: string;
  status: string;
  sellingPrice: number | null;
  reorderPoint: number;
  /** Sellable quantity (summed across locations). */
  available: number;
  /** Physical quantity on hand (summed across locations). */
  onHand: number;
  /** Reserved for open orders (summed across locations). */
  allocated: number;
  /** Whether an `inventory_balances` row exists (false ⇒ "Not tracked", not zero). */
  hasStockRecord: boolean;
  categoryId: string | null;
  variantCount: number;
  variantSku: string | null;
  variantPrices: Array<number | null>;
  reason: string | null;
  categories: Category[];
  brand?: string | null;
  weightKg?: number | null;
  hasSupplier?: boolean;
}

export interface ProductListRowViewModel {
  id: string;
  name: string;
  reference: string | null;
  status: string;
  productSku: string;
  variantCount: number;
  variantSku: string | null;
  reason: string | null;
  price: SellingPriceSummary;
  stockCell: ProductStockCell;
  stockTone: string;
  stockTag: string | null;
  available: number;
  onHand: number;
  allocated: number;
  stockPrimary: string;
  stockSecondary: string;
  categoryPath: string | null;
  attentionReasons: string[];
}

export function buildProductListRow(input: ProductListRowInput): ProductListRowViewModel {
  const price = sellingPriceSummary(input.sellingPrice, input.variantPrices);
  const stockCell = productStockCell(input.hasStockRecord, input.onHand, input.available, input.reorderPoint);
  const stockTone = stockCell === "out" ? "text-red-600" : stockCell === "low" ? "text-amber-600" : "text-slate-700";
  const stockTag = stockCell === "out" ? "Out" : stockCell === "low" ? "Low" : null;
  const categoryPath = categoryPathName(input.categories, input.categoryId) || null;

  const attentionReasons: string[] = [];
  if (input.status === "draft") attentionReasons.push("Incomplete product (draft)");
  if (stockCell === "out") attentionReasons.push("Stock issue: out of stock");
  if (stockCell === "low") attentionReasons.push("Stock issue: low stock");
  if (!input.brand?.trim()) attentionReasons.push("Missing recommended brand");
  if (input.weightKg == null) attentionReasons.push("Missing dimensions");
  if (input.hasSupplier === false) attentionReasons.push("Missing supplier");

  return {
    id: input.id,
    name: input.name,
    reference: input.reference,
    status: input.status,
    productSku: input.sku,
    variantCount: input.variantCount,
    variantSku: input.variantSku,
    reason: input.reason,
    price,
    stockCell,
    stockTone,
    stockTag,
    available: input.available,
    onHand: input.onHand,
    allocated: input.allocated,
    stockPrimary: sellableStockLabel({
      available: input.available,
      onHand: input.onHand,
      allocated: input.allocated,
    }).primary,
    stockSecondary: sellableStockLabel({
      available: input.available,
      onHand: input.onHand,
      allocated: input.allocated,
    }).secondary,
    categoryPath,
    attentionReasons,
  };
}